import {
  Box,
  Card,
  CardActionArea,
  CardProps,
  Container,
  Typography,
  useTheme
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { Stack } from "@mui/system";

import { mergeSx } from "merge-sx";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { CardMediaWithFallback, Warning24Px } from "@qubit/autoparts";

const Quantity = styled(Typography)(() => ({
  fontSize: "1.4em"
}));

type Props = CardProps & {
  /** If you need truncation, use the TruncatedProductName component instead. */
  productName?: string;
  imageFileName: string;
  /** Renders quantity "on hand" below image */
  quantity?: number;
  disableGutters?: boolean;
  /** Optionally render a section under the image. Used for Putaway quantity and date fields */
  bottomSection?: ReactNode;
  onProductImageClick?: () => Promise<boolean>;
  disabled?: boolean;
  warning?: boolean;
  sanitize?: boolean;
  containerSx?: React.CSSProperties;
  /** When set, renders a side ribbon element on the left edge of the card */
  sideRibbon?: ReactNode;
  ineligible?: boolean;
};

export function Root({
  productName,
  imageFileName,
  quantity,
  disableGutters,
  bottomSection,
  onProductImageClick,
  disabled,
  children,
  sx: sxProp,
  warning,
  sanitize,
  containerSx,
  sideRibbon,
  ineligible,
  ...rest
}: Props) {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const sx = {
    position: "relative",
    padding: sideRibbon ? 0 : "16px",
    overflow: "hidden",
    ...(warning && { border: `3px solid ${palette.warning.dark}` }),
    ...(sanitize && { border: `3px solid ${palette.infoPurple.dark}` }),
    ...(ineligible && {
      border: `3px solid ${palette.error.dark}`,
      backgroundColor: palette.error.light
    })
  };

  const content = (
    <Stack
      id="productInfo"
      sx={{
        paddingBottom: "16px",
        ...(ineligible && { opacity: 0.6 })
      }}
      gap={1}
      height="100%"
    >
      {productName && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography role="heading" variant="h5">
            {productName}
          </Typography>
          {warning && <Warning24Px fill={palette.warning.dark} />}
          {sanitize && <Warning24Px fill={palette.infoPurple.dark} />}
        </Stack>
      )}

      {children}

      <CardActionArea
        aria-label="product"
        disabled={disabled}
        onClick={(_) => onProductImageClick && onProductImageClick()}
        sx={{ overflow: "hidden" }}
      >
        <CardMediaWithFallback
          image={imageFileName}
          title="product"
          sx={{ height: "100%", maxHeight: "200px" }}
        />
      </CardActionArea>

      {quantity && (
        <Stack id="quantity" alignItems="flex-end">
          <Quantity
            style={
              sxProp && Object.hasOwn(sxProp, "height")
                ? { position: "absolute", bottom: "0.625em" }
                : {}
            }
          >
            <span style={{ textDecoration: "underline" }}>{`${quantity}`}</span>
            {` ${t("on hand")}`}
          </Quantity>
        </Stack>
      )}

      {bottomSection}
    </Stack>
  );

  return (
    <Container
      disableGutters={disableGutters}
      sx={{ ...(containerSx && { ...containerSx }) }}
    >
      <Card
        sx={mergeSx(sx, sxProp)}
        {...rest}
        role="region"
        aria-label="Product card"
      >
        {sideRibbon ? (
          <Box sx={{ display: "flex", minHeight: "100%" }}>
            {sideRibbon}
            <Box sx={{ flex: 1, minWidth: 0, p: "16px" }}>{content}</Box>
          </Box>
        ) : (
          content
        )}
      </Card>
    </Container>
  );
}
